import { ButtonKind, SpsIcon } from "@spscommerce/ds-shared";
import * as React from "react";

import { SpsButton, SpsInputGroup, SpsTextInput, SpsZeroState } from "../index";

/**
 * For simple examples it seems like using the same code
 * for tests and demo examples is a good idea, but this
 * quickly breaks down as your examples or tests get more
 * complex.
 *
 * Let's look at why this is...
 *
 *  - You may want your demo-able examples to be in JSX even
 *    though your component library and code is in TSX.
 *  - You often want to have classes or functions so JSX literals
 *    which can be converted to strings are not actually enough
 *    for a given example.
 *  - testId is a prop we place on certain parts of our components
 *    to make it easy to perform integration tests on the various
 *    atomic units of a component. We do not want these in the code
 *    for examples but do want them on the components when performing
 *    those tests against actually rendered components using things
 *    like sauce laubs or react-testing-library.
 *
 * For these reasons we're implementing examples with the following
 * rules:
 *
 *  - Each example should be a class with a render method. This render
 *    method represents the renderable code that can be used in tests
 *    or if we actually needed to render this example.
 *  - Each example class should have a reactLive static string that
 *    represents the demo-able code that reactLive will actually
 *    render. Note that this may differ slightly from the tested code
 *    but should mostly cover the same use-cases as the render method.
 *    If the use cases of render and reactLive diverge then you should
 *    consider splitting the example into two examples and two test
 *    scenarios.
 *  - It is possible that in the future we will require a tsx static
 *    class property to support typescript react examples. One hope of
 *    supporting typescript is we would be less likely to need verbose
 *    examples written specifically in typescript, but that remains to
 *    be validated.
 *
 * While having three examples can seem like a pain. We already had
 * these before in our tests and demos, we've just been able to
 * collocate them here to make them easier to review differences.
 *
 * Naturally there is the possibility that they will fall out of sync
 * but that has always been the case in software. Hopefully this
 * design makes it easier to resolve conflicts between tests, jsx and
 * tsx demos.
 */
export class SpsZeroStateBasic1 extends React.Component<{"data-testid": any}> {
    // Used in react-live examples
    static reactLive = {
        code: `
            <SpsZeroState
                heading="No record found"
                subHeading="Find new companies by using the search box above."
                artwork="https://cdn.prod.spsc.io/web/framework/assets/18.03.02/images/icon-error.svg"
            />
        `
    };
    render() {
        const { "data-testid": testId } = this.props;
        // Used in react-testing-library specs
        return (
            <SpsZeroState
                data-testid={testId}
                heading="No record found"
                subHeading="Find new companies by using the search box above."
                artwork="https://cdn.prod.spsc.io/web/framework/assets/18.03.02/images/icon-error.svg"
            />
        );
    }
}

export class SpsZeroStateWithButton1 extends React.Component<{'data-testid': string}, { testResult: any }> {
    // A demo-able representation of this example
    static reactLive = {
        code: `
            <SpsZeroState
                heading="Search for a record"
                subHeading="Find new companies by using the search box above."
                artwork="https://cdn.prod.spsc.io/web/framework/assets/18.03.02/images/icon-empty-search@2x.svg"
            >
                <SpsButton
                    kind="confirm"
                    onClick={() => {
                        console.log("clicked");
                    }}
                >Search Record</SpsButton>
            </SpsZeroState>
    `
    };
    state = {
        testResult: null
    };
    // A testable, executable representation of this example.
    render() {
        const {"data-testid": testId } = this.props;
        const {testResult} = this.state;
        return (
            <>
                <SpsZeroState
                    data-testid={testId}
                    heading="Search for a record"
                    subHeading="Find new companies by using the search box above."
                    artwork="https://cdn.prod.spsc.io/web/framework/assets/18.03.02/images/icon-empty-search@2x.svg"
                >
                    <SpsButton
                        data-testid={`${testId}__button`}
                        kind={ButtonKind.CONFIRM}
                        onClick={() => {
                            this.setState({testResult: "clicked"});
                        }}
                    >Search Record</SpsButton>
                </SpsZeroState>
                <div data-testid="test-result">
                    {testResult}
                </div>
            </>
        );
    }
}

// See below for react-live example
// This one is a bit different since it is a function component
// instead of a class. Same concepts apply except you have the
// ability to use hooks
function SpsZeroStateWithInputGroup1 (props) {
    // Test ids!
    const { "data-testid": testId } = props;
    const [typedSearch, setTypedSearch] = React.useState('');
    const [submittedSearch, setSubmittedSearch] = React.useState(undefined);
    function handleChange(e) {
        setTypedSearch(e.target.value);
    }
    function handleSubmit(e) {
        e.preventDefault();
        setSubmittedSearch(typedSearch);
    }
    React.useEffect(function FormSubmittedEffect() {
        if (submittedSearch) {
            console.log('Search submitted!', submittedSearch);
        }
    }, [submittedSearch]);

    React.useEffect(function SearchTypedEffect() {
        if (typedSearch) {
            console.log('typed', typedSearch);
        }
    }, [typedSearch]);
    return (
        <>
            <SpsZeroState
                data-testid={testId}
                heading="Search for a record"
                subHeading="Find new companies by using the search box below.."
                artwork="https://cdn.prod.spsc.io/web/framework/assets/18.03.02/images/icon-empty-search@2x.svg"
            >
                <form onSubmit={handleSubmit} data-testid={`${testId}__form`}>
                    <SpsInputGroup data-testid={`${testId}__input-group`}>
                        <SpsTextInput
                            name="search"
                            value={typedSearch}
                            placeholder="Search for record"
                            onChange={handleChange}
                            data-testid={`${testId}__text-input`}
                        />
                        <SpsButton icon={SpsIcon.SEARCH} data-testid={`${testId}__button`} />
                    </SpsInputGroup>
                </form>
            </SpsZeroState>
            <div data-testid="test-result">
                {submittedSearch}
            </div>
        </>
    );
}
// @ts-ignore
SpsZeroStateWithInputGroup1.reactLive = {
    code: `
    // This example looks long because of all of the comments but there actually
    // isn't much code.
    //
    // This example uses a function and react hooks instead of using classes.
    // You don't have to use hooks for this but it can make it easier to work
    // with side effects and state in some scenarios. There may be things that
    // are easier to do with classes, any some with hooks. One thing to note
    // before we get started is that I have named all my classes. This works
    // well when debugging or in the dev tools so that you get clean stack traces.
    function Component () {

        // We're going to use React's useState hook here to define a pair
        // the pair is a array. The first item in the array is the value
        // of the state. The second item in the array is a setter for that
        // value.
        const [typedSearch, setTypedSearch] = React.useState('');
        const [submittedSearch, setSubmittedSearch] = React.useState();

        // We can handle the changes when someone types, grabbing the most
        // recent value and updating our state to reflect that key press.
        function handleChange(e) {
            setTypedSearch(e.target.value)
        }

        // Another part of forms is handling their submission.
        //
        // We use submit and not a click handler on the form button because there
        // are many ways to submit forms. The simplest is to simply hit enter while
        // in a text area in the form.
        function handleSubmit(e) {
            // One thing you must do when dealing with form submission that you
            // don't need to do with onClicks is to call e.preventDefault(). This
            // simply prevents the page reloading when someone submits a form. 
            //
            // Why is this needed? HTML form tags were designed for server based
            // form submission. We're not using that so we'll suppress the default.
            e.preventDefault();

            // Finally, we set the submitted search to set of characters currently in
            // the search box.
            setSubmittedSearch(typedSearch);
        }

        // React.useEffect can be thought of a way to do something over and over again
        // when certain things change. Here we are going to console.log the search any
        // time there is a new search.
        React.useEffect(function FormSubmittedEffect() {

          // We only want to log the search if there is actually a search
          // submitted. If it is empty, do nothing.
          if (submittedSearch) {
            console.log('Search submitted!', submittedSearch);
          }

          // The last part of this might look weird but it makes sense if you take a
          // moment to think about it. We are providing an array of things to watch
          // for changes.
          //
          // We want to tell react to re-run our effect over and over again when 
          // certain things change. In this case we only want our effect to run 
          // when the submitted search changes so it is the only variable in this array.
        },[submittedSearch]);

        // Let's use effect one more time, this time we'll use it to console log when
        // the actual search term entered is changed (before submission).
        React.useEffect(function SearchTypedEffect() {
          if (typedSearch) {
            console.log('typed', typedSearch);
          }
        }, [typedSearch]);

        return (
            <SpsZeroState
              heading="Search for a record"
              subHeading="Find new companies by using the search box below.."
              artwork="https://cdn.prod.spsc.io/web/framework/assets/18.03.02/images/icon-empty-search@2x.svg"
            >
               <form onSubmit={handleSubmit}>
                    <SpsInputGroup>
                        <SpsTextInput
                           name="search"
                           value={typedSearch}
                           placeholder="Search for record"
                           onChange={handleChange}
                        />
                        <SpsButton kind="default" icon="search" />
                    </SpsInputGroup>
                </form>
            </SpsZeroState>
        );
      }
    render(<Component />)
    `
};

export {SpsZeroStateWithInputGroup1};
